/**
 * TvCastPicker — the desktop "Play on…" control (lives in the Visualizer's top-right menu, next to
 * the SourcePicker). Deterministic push cast, mirroring the mobile/iOS pickers: expand → the Rust side
 * browses `_hzp-tv._tcp` for TV receivers → pick one → the capture's PCM is PUSHED to it. "This
 * computer" stops the push. The legacy broadcast (pull — older TV apps auto-discover this computer)
 * stays available as the last row, with its URL + stop flow unchanged.
 */
import React, { useEffect, useRef, useState } from 'react'
import { track } from './analytics'
import { currentSourceId, startCast, stopNative, type CastInfo } from './audioSession'
import {
  tvcastBrowseStart, tvcastBrowseStop, tvcastConnect, tvcastDevices, tvcastDisconnect, pokeCastStatus, watchCastStatus,
  type TvCastStatus, type TvDevice
} from './cast/sender'

const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
  background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: 6, padding: '5px 9px', font: '11px monospace', cursor: 'pointer'
}

export function TvCastPicker(): JSX.Element {
  const [open, setOpen] = useState(false)
  const [devices, setDevices] = useState<TvDevice[]>([])
  const [status, setStatus] = useState<TvCastStatus>({ state: 'idle', name: null, error: null })
  const [notice, setNotice] = useState<string | null>(null)
  const [castInfo, setCastInfo] = useState<CastInfo | null>(null) // legacy broadcast (pull)
  const noticeTimer = useRef(0)

  // browse + poll the device list while the picker is open
  useEffect(() => {
    if (!open) return
    void tvcastBrowseStart()
    const tick = (): void => { void tvcastDevices().then(setDevices) }
    tick()
    const t = window.setInterval(tick, 1200)
    return () => { window.clearInterval(t); void tvcastBrowseStop() }
  }, [open])

  // the cast status (the page's one poll, cast/sender.ts): reflects connecting → connected, and surfaces a drop
  // ("TV disconnected"); fast while the picker is open
  const openRef = useRef(open)
  openRef.current = open
  useEffect(() => { if (open) pokeCastStatus() }, [open])
  useEffect(() => watchCastStatus((s) => {
    setStatus(s)
    if (s.error) {
      setNotice(s.error)
      window.clearTimeout(noticeTimer.current)
      noticeTimer.current = window.setTimeout(() => setNotice(null), 4000)
    }
  }, () => openRef.current), [])

  const casting = status.state === 'connected'
  const label = casting ? `📺 ${status.name}` : status.state === 'connecting' ? '📺 connecting…' : '📺 Play on…'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'rgba(86,140,220,0.22)', color: '#fff', border: '1px solid rgba(86,140,220,0.5)', borderRadius: 6, padding: '5px 9px', font: '11px monospace', cursor: 'pointer', textAlign: 'left' }}
        title="play the visuals on a SYQEL Art TV on your network"
      >{label}{open ? '  ▴' : '  ▾'}</button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button style={row} onClick={() => { void tvcastDisconnect(); setStatus({ state: 'idle', name: null, error: null }) }}>
            💻 This computer {status.state === 'idle' && !castInfo ? ' ✓' : ''}
          </button>
          {devices.map((d) => (
            <button
              key={d.name}
              style={{ ...row, ...(status.name === d.name ? { border: '1px solid rgba(124,91,255,0.8)', background: 'rgba(124,91,255,0.25)' } : {}) }}
              onClick={() => { void tvcastConnect(d).then(pokeCastStatus); setStatus({ state: 'connecting', name: d.name, error: null }); track('cast_start', { method: 'push' }) }}
            >📺 {d.name}{status.name === d.name && casting ? ' ✓' : ''}</button>
          ))}
          {devices.length === 0 && (
            <div style={{ font: '10.5px monospace', opacity: 0.6, padding: '2px 2px' }}>searching your Wi-Fi for a SYQEL Art TV…</div>
          )}
          {/* legacy pull broadcast — older TV apps auto-discover this computer and connect */}
          {!castInfo ? (
            <button
              style={{ ...row, opacity: 0.75 }}
              title="older TV app? broadcast this audio — the TV finds this computer automatically"
              onClick={() => { void startCast(currentSourceId() || 'system').then((i) => { if (i) { setCastInfo(i); track('cast_start', { method: 'broadcast' }) } }) }}
            >📡 Broadcast mode (older TVs)</button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10.5, lineHeight: 1.45 }}>
              <span style={{ opacity: 0.7 }}>Broadcasting — or open this URL on the TV:</span>
              <code style={{ wordBreak: 'break-all', color: '#9fe8c8' }}>{
                (() => {
                  const qs: string[] = []
                  if (castInfo.port !== 7780) qs.push(`p=${castInfo.port}`)
                  if (castInfo.token) qs.push(`token=${castInfo.token}`)
                  return `http://${castInfo.ip}:5173/tv.html${qs.length ? `?${qs.join('&')}` : ''}`
                })()
              }</code>
              <button
                onClick={() => { stopNative(); setCastInfo(null) }}
                style={{ background: 'rgba(255,120,160,0.18)', color: '#fff', border: '1px solid rgba(255,120,160,0.45)', borderRadius: 6, padding: '4px 8px', font: '10.5px monospace', cursor: 'pointer', alignSelf: 'flex-start' }}
              >stop broadcast</button>
            </div>
          )}
        </div>
      )}

      {notice && <div style={{ font: '10.5px monospace', color: '#ff9d9d' }}>{notice}</div>}
    </div>
  )
}
